// src/lib/environment.ts
//
// What this build is allowed to SAY about itself, derived from the gateway's
// own report of **which identity provider it installed** — with its `APP_ENV`
// carried alongside as context, never as the claim.
//
// ══ THE FOURTH DEFECT (this change): ONE BADGE, TWO CLAIMS, ONE WITNESS ══
//
// The notice asserted, in a single breath and as flat fact:
//
//     "no real Authority data is used, AND nothing you do here reaches a live
//      payment agency"
//
// Those are two claims about two different things, and until now BOTH rested on
// one field — first `environment`, then `identityProvider`. Neither field is
// evidence for either half on its own.
//
//   * **The payment-agency half is now witnessed.** The gateway reports
//     `settlementDispatch: 'none' | 'simulated' | 'live'` on `/api/identities`,
//     `POST /api/session` and `/api/me`, set from the branch in `buildServer`
//     where a Payment Agency adapter would be constructed. It is falsifiable, not
//     asserted: `services/gateway/src/settlement-dispatch.test.ts` fails the build
//     if any outbound call appears in the server packages while it still says
//     `none`. `'none'`/`'simulated'`, narrowed through `isSettlementDispatch`, is
//     the ONLY licence to print that clause. `'live'`, an unrecognised value, and
//     "it answered without saying" all withdraw it.
//
//   * **The data half has no witness anywhere, and the server declines to invent
//     one.** `packages/contracts/src/settlement-dispatch.ts` states why: this
//     gateway connects to whatever `DATABASE_URL` names, and a marker written by
//     the synthetic seed would still be there after real data was loaded beside
//     it — a safety claim that outlives the world it describes, which is S-016's
//     own defect class rebuilt one layer down rather than a fix for it. So the
//     sentence is GONE. What replaces it is the true statement of the limit: this
//     build cannot see inside that database, and the deployment name is an
//     intention rather than a check.
//
// ⚠ The direction is the point, again. Dropping a reassurance costs an officer
// some comfort. Keeping an unwitnessed one costs an officer who treats live
// records as fixtures. Only one of those is recoverable.
//
// ══ THE THIRD DEFECT: IT FAILED OPEN WHEN THE FIELD WAS ABSENT ══
//
// The capability axis was right and its narrowing was not. `identityProvider`
// was typed `string` on three response interfaces and `lib/api.ts` produced
// those interfaces with a bare `JSON.parse` cast, so a response that simply
// OMITTED the field arrived as `undefined` — and `undefined`, `null` and `''`
// all mapped to `{ kind: 'unknown' }`, which renders no badge and no notice.
//
// A gateway still running the passwordless demo stub over synthetic seed data
// but not reporting the field — an older gateway behind a newer bundle, a proxy
// that strips it, a body carrying `""` — therefore showed the officer NOTHING.
// That is worse than the `environment`-driven code this replaced, which would at
// least have rendered "SIT / DEMO", and it is the precise failure direction the
// contract forbids in as many words: "an unrecognised value is dangerous and
// must be treated as such by any client".
//
// The fix is to stop `null` meaning two things. `IdentityProviderReport`
// (`packages/contracts`) can only be constructed from a response, so
// **"no response yet" is the absence of a report** and "a response without the
// field" is a report of `null` — a different value, with its own `absent`
// disclosure and its own danger notice. `lib/api.ts` builds the report at the
// boundary for every response that carries the field, so a new call site cannot
// reach this function with an unvalidated string at all: the compiler refuses
// it.
//
// ══ THE SECOND DEFECT ══
//
// The badge and the synthetic-data notice branched on `environment`. The
// gateway has since started reporting `identityProvider` on `/api/identities`,
// `/api/me` and `POST /api/session`, and `packages/contracts/src/identity-
// provider.ts` — the contract for that field — says in as many words that
// `environment` is a DEPLOYMENT NAME and carries no information about what is
// authenticating anyone. Nothing in this app read the new field: `grep -rn
// identityProvider apps/officer-web/src/` returned nothing.
//
// That is not a tidiness point. `identityProvider: 'federated'` with
// `APP_ENV=sit` — a real identity provider in the SIT deployment, which is
// exactly what SIT becomes the day federation lands — rendered "no real
// Authority data is used, and nothing you do here reaches a live payment
// agency". That is the one claim this control must never make falsely, stated
// in this file's own header below, and the environment axis could not tell the
// difference. The claim now turns on the CAPABILITY that makes it true.
//
// ══ THE DEFECT THIS REPLACES (I8) ══
//
// `Shell.tsx` and `LoginPage.tsx` both tested one exact string —
// `environment !== 'production'` — and on that single comparison they rendered
// a "SIT / DEMO" badge AND an alert asserting "no real Authority data is used,
// and nothing you do here reaches a live payment agency".
//
// That is an ALLOW-LIST question answered with a DENY-LIST, and it fails in the
// worst possible direction: `prod`, `PRODUCTION`, `live`, or a typo in a
// deployment variable all fall through to "not production" and the client
// cheerfully states, in an alert box, that nothing on screen is real. A false
// statement of safety is the one failure this particular control must never
// have — a true one costs a cosmetic badge, a false one costs an officer
// treating live money as a demo.
//
// The gateway already has the right discipline: `identity.ts`'s
// `ALLOWED_ENVIRONMENTS` is an allow-list, and it refuses to boot on an
// unrecognised value precisely "because a typo in an environment variable must
// not be what stands between a demo login and production". This module gives
// the client the same discipline: recognise what is known, and treat anything
// else as the dangerous case rather than the safe one.
//
// ══ WHAT IS DELIBERATELY *NOT* DONE HERE ══
//
// **No case folding, no trimming, no normalisation.** The server compares
// exact strings against its own allow-list; a client that lower-cased
// `PRODUCTION` into a match would be more permissive than the server it is
// describing, which is how the deny-list got this wrong in the first place.
// `SIT` (uppercase) is not `sit` — to the gateway it is a refusal to boot, and
// to this module it is an unrecognised environment. Those agree, which is the
// point.
//
// **The badge is not deleted.** It was asked for as environment-driven, and
// this build genuinely does run the passwordless federated-identity stub
// (`services/gateway/src/identity.ts`) over synthetic seed data
// (`005_seed_synthetic.sql`). The badge is true here; what changed is that it
// is now only shown where it can be proven true.
//
// **The environment is not deleted either, and is not the claim.** It is still
// reported, still allow-listed by `discloseEnvironment` below, and still shown
// — inside the notice's own text, as context an officer can quote to the
// service desk. What it no longer does is decide whether the notice appears.

import {
  isIdentityProvider,
  isNotificationDelivery,
  isSettlementDispatch,
  type IdentityProviderReport,
  type NotificationDeliveryReport,
  type SettlementDispatchReport,
} from '@prs2/contracts'

/**
 * The environments the gateway will actually start in — read from
 * `ALLOWED_ENVIRONMENTS` in `services/gateway/src/identity.ts`, not invented.
 *
 * ⚠ **This set is CONTEXT, not evidence.** It says which deployment names the
 * gateway admits; it says nothing about what is in the database that deployment
 * connects to. The old comment here claimed "the data is the synthetic seed",
 * which is exactly the unlicensed half of the sentence S-016 removed — a
 * deployment name cannot see inside a database.
 */
const DEMO_ENVIRONMENTS: ReadonlySet<string> = new Set(['development', 'sit', 'test'])

/**
 * The environments in which this build makes no claim at all.
 *
 * ⚠ Today the gateway cannot boot with `APP_ENV=production`
 * (`assertDemoIdentityAllowed` is the first line of `buildServer`), so this
 * branch is unreachable through a running gateway — see the note in the task
 * report. It is kept because the value is reachable the moment the demo
 * identity provider is replaced by real federation, and because a client that
 * had to be edited at that moment is a client that would not be.
 */
const PRODUCTION_ENVIRONMENTS: ReadonlySet<string> = new Set(['production'])

export type EnvironmentDisclosure =
  /** The server has not reported yet — nothing is claimed either way. */
  | { readonly kind: 'unknown' }
  /** A recognised non-production environment: demo identities, synthetic data. */
  | { readonly kind: 'demo'; readonly reported: string }
  /** A recognised production environment: no badge, no notice, no claim. */
  | { readonly kind: 'production'; readonly reported: string }
  /** Anything else. Treated as the dangerous case, never as the safe one. */
  | { readonly kind: 'unrecognised'; readonly reported: string }

export function discloseEnvironment(environment: string | null | undefined): EnvironmentDisclosure {
  if (environment === null || environment === undefined || environment === '') return { kind: 'unknown' }
  if (DEMO_ENVIRONMENTS.has(environment)) return { kind: 'demo', reported: environment }
  if (PRODUCTION_ENVIRONMENTS.has(environment)) return { kind: 'production', reported: environment }
  return { kind: 'unrecognised', reported: environment }
}

/**
 * What the gateway says is authenticating its users, narrowed at the boundary.
 *
 * ⚠ The allow-list is {@link isIdentityProvider}, imported from
 * `@prs2/contracts` — the guard the contract exports for exactly this — and NOT
 * a second local copy of the two strings. A local copy is how the environment
 * allow-list and the gateway's `ALLOWED_ENVIRONMENTS` would have drifted, and
 * the whole discipline this module documents is "recognise what is known, treat
 * anything else as the dangerous case".
 *
 * `unrecognised` is not a shrug. "I do not know what is authenticating these
 * people" is a reason to warn LOUDER, never to fall silent — the contract says
 * so, and the direction of that failure is the entire subject of this file.
 *
 * ⚠ `unknown` and `absent` ARE NOT THE SAME STATE and folding them together is
 * the third defect this file records. `unknown` is reachable only before a
 * response exists; `absent` means a gateway answered and did not say what is
 * signing anyone in, which is a fact about that gateway and belongs in the same
 * bucket as `unrecognised`.
 */
export type ProviderDisclosure =
  /**
   * No response has arrived. Nothing is claimed either way — and this is the
   * ONLY state that stays silent, because it is the only one with no gateway
   * behind it to describe.
   */
  | { readonly kind: 'unknown' }
  /** `demo-stub`: passwordless identities over synthetic seed data. */
  | { readonly kind: 'demo-stub' }
  /** `federated`: a real identity provider. No synthetic-data claim is available. */
  | { readonly kind: 'federated' }
  /**
   * A response arrived carrying no usable value — the field absent, `null`, a
   * non-string, or `''`. Treated as the dangerous case: the server spoke and
   * said nothing, so this build knows exactly as much about what is signing
   * people in as it does for a value it does not recognise.
   */
  | { readonly kind: 'absent' }
  /** Anything else. Treated as the dangerous case, never as the safe one. */
  | { readonly kind: 'unrecognised'; readonly reported: string }

/**
 * ⚠ Takes the REPORT, not a string. A caller with no response has no report and
 * passes `null`; a caller with a response always has one, whatever it said. The
 * signature is what makes "not asked yet" unreachable from a real response body
 * — it used to be reachable, and an empty string reached it.
 */
export function discloseIdentityProvider(report: IdentityProviderReport | null): ProviderDisclosure {
  if (report === null) return { kind: 'unknown' }
  if (report.reported === null) return { kind: 'absent' }
  if (!isIdentityProvider(report.reported)) return { kind: 'unrecognised', reported: report.reported }
  return report.reported === 'demo-stub' ? { kind: 'demo-stub' } : { kind: 'federated' }
}

/**
 * Whether this build may say that nothing done here reaches a live Payment
 * Agency — the SECOND claim in the notice's sentence, and the only one of the
 * two the server can witness.
 *
 * ⚠ Deliberately the same five-state shape as {@link ProviderDisclosure}, for
 * the reason S-017 records rather than for symmetry. `unknown` (no report at
 * all) and `absent` (a response that did not say) are different facts about
 * different worlds, and folding them together is precisely how a live demo stub
 * came to render no warning. Here the stake is the mirror image: folding them
 * would let a gateway that never mentioned the field license a reassurance.
 *
 * ⚠ **`contained` is the only state that licenses the clause.** `'none'` and
 * `'simulated'` are grouped because they answer the officer's question
 * identically — neither can instruct a real Payment Agency — while keeping the
 * reported value so the sentence can say WHICH, since "no interface is wired"
 * and "the interface points at a simulator" are different things to report to a
 * service desk.
 */
export type DispatchDisclosure =
  /** No report at all: no response has arrived. Nothing is claimed either way. */
  | { readonly kind: 'unknown' }
  /** `none` or `simulated` — nothing here can instruct a real Payment Agency. */
  | { readonly kind: 'contained'; readonly reported: 'none' | 'simulated' }
  /** `live` — a real Payment Agency interface is installed. The clause is false. */
  | { readonly kind: 'reaching' }
  /** A response arrived carrying no usable value. Not evidence of containment. */
  | { readonly kind: 'absent' }
  /** Anything else. Treated as the dangerous case, never as the safe one. */
  | { readonly kind: 'unrecognised'; readonly reported: string }

/**
 * ⚠ Takes the REPORT, not a string — same signature discipline as
 * {@link discloseIdentityProvider}, and for the same reason: a caller with no
 * response has no report, and a caller with a response always has one whatever
 * it said. `isSettlementDispatch` does the narrowing; nothing here casts.
 */
export function discloseSettlementDispatch(report: SettlementDispatchReport | null): DispatchDisclosure {
  if (report === null) return { kind: 'unknown' }
  if (report.reported === null) return { kind: 'absent' }
  if (!isSettlementDispatch(report.reported)) return { kind: 'unrecognised', reported: report.reported }
  if (report.reported === 'live') return { kind: 'reaching' }
  return { kind: 'contained', reported: report.reported }
}

/**
 * ★★ THE FINAL INTEGRATION REVIEW'S CRITICAL — the FOURTH argument, and it is
 * here for a defect that had already happened rather than for symmetry.
 *
 * The 2026-08-15 notification-delivery wave added a SECOND outbound capability
 * (`notificationDelivery`, reported on the same three responses as the other
 * three) and did not extend this module to it: `environmentBanner` still took
 * three arguments, and `grep -rn notificationDelivery apps/officer-web/src/`
 * found the field only in `api.ts`, `types.ts` and the notifications screen.
 * **That is verbatim the defect this file's own header records for
 * `identityProvider`** — "nothing in this app read the new field" — reintroduced
 * one capability over, on the axis where it is worse: the settlement branch
 * below calls a passwordless roster beside a live counterparty interface
 * *"strictly worse than the production-name case"*, and a live MAIL relay is
 * the same shape with the same unauthenticated session behind it.
 *
 * ⚠ The gateway now REFUSES TO BOOT on that pair (`assertDemoIdentityMailSafe`,
 * `services/gateway/src/identity.ts`), which is the primary control. This
 * disclosure is the client's own check on it, and it is not redundant for the
 * reason every other field here is not: a bundle is copied between deployments
 * far more casually than configuration is, so the gateway in front of this
 * bundle may be OLDER than the refusal. A client that trusted a server-side
 * guard it cannot see would be trusting the very thing it exists to check.
 *
 * ⚠ Same five-state shape and same narrowing discipline as
 * {@link DispatchDisclosure}: `unknown` (no report) and `absent` (a response
 * that did not say) stay different facts, and only `contained` is quiet.
 */
export type DeliveryDisclosure =
  /** No report at all: no response has arrived. Nothing is claimed either way. */
  | { readonly kind: 'unknown' }
  /** `none` or `simulated` — nothing queued here can reach a real mailbox. */
  | { readonly kind: 'contained'; readonly reported: 'none' | 'simulated' }
  /** `live` — a transport that genuinely reaches a relay is wired. */
  | { readonly kind: 'reaching' }
  /** A response arrived carrying no usable value. Not evidence of containment. */
  | { readonly kind: 'absent' }
  /** Anything else. Treated as the dangerous case, never as the safe one. */
  | { readonly kind: 'unrecognised'; readonly reported: string }

/**
 * ⚠ Takes the REPORT, not a string — same signature discipline as
 * {@link discloseSettlementDispatch}, and for the same reason.
 * `isNotificationDelivery` does the narrowing; nothing here casts.
 */
export function discloseNotificationDelivery(report: NotificationDeliveryReport | null): DeliveryDisclosure {
  if (report === null) return { kind: 'unknown' }
  if (report.reported === null) return { kind: 'absent' }
  if (!isNotificationDelivery(report.reported)) return { kind: 'unrecognised', reported: report.reported }
  if (report.reported === 'live') return { kind: 'reaching' }
  return { kind: 'contained', reported: report.reported }
}

export interface EnvironmentNotice {
  /** Maps to `SgdsAlert`'s `variant`. */
  readonly variant: 'warning' | 'danger'
  readonly title: string
  readonly body: string
}

export interface EnvironmentBanner {
  /** Text for the brand band's `.pt-env-badge` pill, or `null` for no pill. */
  readonly badge: string | null
  /** The alert under the brand band, or `null` for none. */
  readonly notice: EnvironmentNotice | null
}

/**
 * The environment, phrased for a sentence — context inside a notice, never the
 * reason the notice exists.
 */
function environmentClause(environment: string | null | undefined): string {
  const disclosure = discloseEnvironment(environment)
  switch (disclosure.kind) {
    case 'unknown':
      return 'The gateway has not reported which deployment this is.'
    case 'demo':
    case 'production':
      return `The gateway reports this deployment as "${disclosure.reported}".`
    case 'unrecognised':
      return `The gateway also reports a deployment name this build does not recognise ("${disclosure.reported}").`
  }
}

/**
 * What may be said about reaching a Payment Agency — one sentence, chosen by the
 * only field that witnesses it.
 *
 * ⚠ **Exactly one branch here is a reassurance, and it is the one the server
 * vouches for.** Every other branch states the limit instead and tells the
 * officer which way to err. That asymmetry is the whole fix: the sentence used
 * to be printed unconditionally alongside a claim about the data, with nothing
 * behind either.
 */
function paymentAgencyClause(dispatch: DispatchDisclosure): string {
  switch (dispatch.kind) {
    case 'contained':
      return dispatch.reported === 'none'
        ? 'The gateway reports that no payment-agency interface is installed, so nothing you do here reaches a live payment agency.'
        : 'The gateway reports its payment-agency interface as a simulator, so nothing you do here reaches a live payment agency.'
    case 'reaching':
      return 'The gateway reports a LIVE payment-agency interface: instructions raised here are acted on by a real Payment Agency.'
    case 'absent':
      return 'The gateway answered without saying whether a payment-agency interface is installed, so this build cannot tell you that your actions stop short of one: assume they do not.'
    case 'unrecognised':
      return `The gateway reports its payment-agency interface as "${dispatch.reported}", which is not a value this build recognises, so it cannot tell you that your actions stop short of a live payment agency: assume they do not.`
    case 'unknown':
      return 'This build has no report of whether a payment-agency interface is installed, so it cannot tell you that your actions stop short of a live payment agency: assume they do not.'
  }
}

/**
 * What may be said about the DATA — which is: what this build can and cannot
 * see, and nothing more.
 *
 * ⚠ **There is no reassuring branch here at all, by design.** The removed
 * sentence — *"Every bill, officer and entitlement value shown here is
 * synthetic: no real Authority data is used"* — had no witness on the server or
 * anywhere else, and the obvious witness (a marker written by the synthetic
 * seed) would survive real data being loaded beside it. So this function reports
 * the limit and, where the deployment name is one the gateway admits, attributes
 * the *intent* to it explicitly as an intent. An intention is not a check, and
 * the sentence says so rather than letting the reader infer it.
 */
/**
 * The "treat it as real" instruction for the two notices that cannot vouch for
 * the identity provider — phrased for how much is ACTUALLY unknown.
 *
 * ⚠ It used to say *"…or that your actions stop short of a live payment agency
 * — so treat BOTH as real"* unconditionally. Once `settlementDispatch` exists,
 * that is a claim of ignorance the build no longer has: a gateway reporting
 * `'none'` has answered the payment half exactly, and repeating "we cannot tell"
 * over an answer is the same disregard for the evidence as the reassurance this
 * change removed, pointing the other way. The identity half is still unknown in
 * both branches, so that half of the instruction never softens.
 */
function unverifiedInstruction(dispatch: DispatchDisclosure): string {
  const trailer = 'and treat any identity you are signed in as as unverified.'
  return dispatch.kind === 'contained'
    ? `Nothing here can confirm that the data on screen is synthetic, so treat it as real, ${trailer}`
    : `Nothing here can confirm that the data on screen is synthetic, or that your actions stop short of a live payment agency, so treat both as real, ${trailer}`
}

function syntheticDataClause(environment: string | null | undefined): string {
  const limit =
    'Nothing here inspects the database this gateway is connected to, so this build cannot tell you whether the ' +
    'records on screen are real.'
  const disclosure = discloseEnvironment(environment)
  return disclosure.kind === 'demo'
    ? `${limit} A deployment named "${disclosure.reported}" is intended to carry synthetic seed data: that is the deployment's intent, not something checked here.`
    : `${limit} Treat every bill, officer and entitlement value as if it could be real.`
}

/**
 * What the brand band and the alert beneath it should say.
 *
 * ⚠ **The CAPABILITY decides, the deployment name does not.** A notice exists at
 * all when — and only when — the identity provider is something this build must
 * qualify, so `identityProvider` is the first argument. `environment` is passed
 * for context and appears inside the body; it cannot turn any claim on or off.
 * See this file's header for the failure that ordering prevents.
 *
 * ⚠ **ONE ARGUMENT PER CLAIM, and that is why there are now four.** The body
 * makes two statements about safety and they have different witnesses:
 * `settlementDispatch` is the only licence for the payment-agency clause, and
 * NOTHING licenses a claim that the data is synthetic — so that sentence is gone
 * and `syntheticDataClause` states the limit instead. A single argument deciding
 * both is the shape of every defect in this file's header.
 *
 * ⚠ **The fourth, `notificationDelivery`, decides an ALARM rather than a
 * clause** — see {@link discloseNotificationDelivery} for why it was missing and
 * what that omission admitted. It opens the `DEMO IDENTITIES WITH LIVE MAIL`
 * danger branch and does nothing else: the ordinary demo notice deliberately
 * gains no fourth sentence about mail, because that notice's other clauses exist
 * to WITHDRAW reassurances this file used to print unconditionally, and it never
 * printed one about mail. Silence about a capability is not a claim about it;
 * a fourth sentence on every demo screen would be noise, and noise is what makes
 * the three danger notices invisible when they matter.
 *
 * ⚠ The unrecognised badge deliberately reuses the SAME pill styling as the
 * demo badge rather than introducing a red one. `.pt-env-badge` sits on the PSD
 * navy band and is drawn in `currentColor` (white); a danger-red pill on navy
 * would be a new token, unmeasured against `#00405F`, on the one surface where
 * `sgds-reconciliation.md` §3a already warns that a second colour is hard to
 * separate. The WORD carries the signal on the band, and the colour carries it
 * in the danger alert directly below, which sits on the page canvas where the
 * SGDS danger tokens are already measured.
 */
export function environmentBanner(
  identityProvider: IdentityProviderReport | null,
  settlementDispatch: SettlementDispatchReport | null,
  environment: string | null | undefined,
  notificationDelivery: NotificationDeliveryReport | null,
): EnvironmentBanner {
  const provider = discloseIdentityProvider(identityProvider)
  const dispatch = discloseSettlementDispatch(settlementDispatch)
  const delivery = discloseNotificationDelivery(notificationDelivery)
  switch (provider.kind) {
    case 'unknown':
      // Nothing is shown before the server has spoken. The previous code
      // treated `null` as "not production" and rendered the safety claim during
      // that window — asserting synthetic data before anything had said so.
      //
      // ⚠ This branch is now reachable ONLY from `null` — the absence of a
      // report. It used to be reachable from `undefined` and `''` as well, i.e.
      // from a real response body, which is how a live demo stub came to render
      // no badge and no warning at all. See `absent` below.
      return { badge: null, notice: null }

    case 'demo-stub': {
      // ⚠ A demo identity provider in a deployment that calls itself production
      // is a contradiction, and the honest answer is to report BOTH halves
      // loudly rather than pick whichever is more comfortable. It should be
      // unreachable — `assertDemoIdentityAllowed` is the first line of
      // `buildServer` — but a client that trusted that would be trusting the
      // very thing it is here to check.
      if (discloseEnvironment(environment).kind === 'production') {
        return {
          badge: 'DEMO IDENTITIES IN PRODUCTION',
          notice: {
            variant: 'danger',
            title: 'Demo identities are enabled on a production deployment',
            body:
              'The gateway reports that anyone can be signed in as anyone, with no credential, and it ' +
              'reports its deployment as production at the same time. Those two statements cannot both be ' +
              'safe. Stop using this build and report it to the service desk immediately; nothing here can ' +
              `tell you whether the records on screen are real. ${paymentAgencyClause(dispatch)}`,
          },
        }
      }

      // ⚠ THE SECOND CONTRADICTION, and it is the more expensive one. A
      // passwordless roster — anyone signed in as anyone, no credential — with a
      // LIVE payment-agency interface behind it means an unauthenticated session
      // can raise instructions a real Payment Agency acts on. That is strictly
      // worse than the production-name case above, which is only a naming
      // contradiction. It cannot arise today (`settlementDispatch` is `'none'`
      // and a sweep fails the build if any outbound call appears while it says
      // so), and it is written now for the same reason `federated` was: a client
      // that first meets this value in production has to guess.
      if (dispatch.kind === 'reaching') {
        return {
          badge: 'DEMO IDENTITIES WITH LIVE SETTLEMENT',
          notice: {
            variant: 'danger',
            title: 'Demo identities can instruct a real Payment Agency',
            body:
              'The gateway reports that anyone can be signed in as anyone, with no credential, and that a ' +
              'live payment-agency interface is installed at the same time. Instructions raised from a ' +
              'session nobody authenticated would be acted on by a real Payment Agency. Stop using this ' +
              `build and report it to the service desk immediately. ${environmentClause(environment)}`,
          },
        }
      }

      // ⚠★ THE THIRD CONTRADICTION, added by the final integration review of
      // the 2026-08-15 notification-delivery wave, and modelled line for line
      // on the settlement branch immediately above because it is the same
      // failure one outbound capability over. A passwordless roster — anyone
      // signed in as anyone, no credential — beside a mail transport that
      // genuinely reaches a relay means an unauthenticated session can put
      // real mail in a real officer's inbox: `mayAdministerNotificationRules`
      // admits `PSD_USER`, which gates `POST /api/notifications/outbox/run`,
      // and every identity on the demo roster is one `POST /api/session` away.
      //
      // ⚠ Checked BELOW the settlement branch, not above it, because a
      // deployment carrying both should be reported by the one with the larger
      // consequence — an instruction a Payment Agency acts on outranks a
      // message an officer receives — and the two badges cannot both be shown.
      //
      // ⚠ It cannot arise from a current gateway at all: `buildServer` refuses
      // to boot on this exact pair (`assertDemoIdentityMailSafe`). It is
      // written for the gateway that is OLDER than this bundle, the same reason
      // the branch above was written while `settlementDispatch` could only ever
      // be `'none'`.
      if (delivery.kind === 'reaching') {
        return {
          badge: 'DEMO IDENTITIES WITH LIVE MAIL',
          notice: {
            variant: 'danger',
            title: 'Demo identities can send real email',
            body:
              'The gateway reports that anyone can be signed in as anyone, with no credential, and that a ' +
              'LIVE outbound mail relay is wired at the same time. A notification run started from a session ' +
              'nobody authenticated would deliver real email to real recipients. Stop using this build and ' +
              `report it to the service desk immediately. ${paymentAgencyClause(dispatch)} ` +
              `${environmentClause(environment)}`,
          },
        }
      }

      // ⚠ The title no longer asserts "Synthetic data only". That was the
      // unwitnessed half of the sentence stated as a heading, which is the
      // loudest place to state it and the one place nobody reads sceptically.
      return {
        badge: 'DEMO IDENTITIES',
        notice: {
          variant: 'warning',
          title: 'Demo identities: anyone can be signed in as anyone',
          body:
            'This build signs you in through a passwordless demo identity provider: any identity on the ' +
            'roster can be assumed with no credential, so no identity on screen is verified. ' +
            `${syntheticDataClause(environment)} ${paymentAgencyClause(dispatch)} ${environmentClause(environment)}`,
        },
      }
    }

    case 'federated':
      // A real identity provider. No badge and no notice: this control exists to
      // qualify a demo build's claims about itself, and against a real provider
      // there is no such claim to qualify, whatever the deployment is called.
      //
      // ⚠ `settlementDispatch` deliberately does NOT open a notice here. A live
      // payment-agency interface behind a real identity provider is the intended
      // production shape, not a contradiction — the alarm above is about a
      // PASSWORDLESS roster reaching one. A banner on every production screen
      // saying "this reaches a payment agency" would be noise, and noise is what
      // makes the two danger notices above invisible when they matter.
      return { badge: null, notice: null }

    case 'absent':
      // ⚠ THE FAIL-OPEN THIS CLOSES. A response that omits the field, sends
      // `null`, sends a non-string, or sends `""` used to land in `unknown`
      // above and render nothing — on a build whose gateway may well be the
      // passwordless demo stub over synthetic seed data. The officer saw a
      // clean screen and had no way to know.
      //
      // The badge and the danger variant are deliberately IDENTICAL to the
      // unrecognised case, because from the officer's side the two states are
      // the same one: this build cannot tell what is signing them in. Only the
      // body differs, and it differs by naming the causes worth checking.
      return {
        badge: 'IDENTITY PROVIDER UNVERIFIED',
        notice: {
          variant: 'danger',
          title: 'This build cannot tell what is signing you in',
          body:
            'The gateway answered without saying which identity provider it installed. ' +
            `${unverifiedInstruction(dispatch)} ${paymentAgencyClause(dispatch)} A ` +
            'gateway older than this build, or a proxy that drops the field, both look exactly like this. Do ' +
            'not use this as a demonstration or a training environment, and report it to the service desk. ' +
            `${environmentClause(environment)}`,
        },
      }

    case 'unrecognised':
      return {
        badge: 'IDENTITY PROVIDER UNVERIFIED',
        notice: {
          variant: 'danger',
          title: 'This build cannot tell what is signing you in',
          body:
            `The gateway reports its identity provider as "${provider.reported}", which is not one this build ` +
            `recognises. ${unverifiedInstruction(dispatch)} ${paymentAgencyClause(dispatch)} Do not use this ` +
            'as a demonstration or a training environment, and report the reported value to the service ' +
            `desk. ${environmentClause(environment)}`,
        },
      }
  }
}

// ══ TASK 5f — demonstrationBanner()/combinedNotice() DELETED, NOT HIDDEN ══
//
// This file used to also export `discloseDemonstrationMode`/`demonstrationBanner`
// (Task 5a) and `combinedNotice` (Task 5e): together they were the ONLY thing
// that made `Shell.tsx` render a "this is a live demonstration"/"demo
// identities" badge and alert on every authenticated route. The Product Owner
// decided no clause in the tender requires an on-screen synthetic-data or
// demonstration notice, and PSD must see the production view as closely as
// possible — so `Shell.tsx` no longer calls any of them, which made all three
// genuinely unreachable code (checked: no other file in this app imported
// them). They are deleted here rather than left as an unreferenced derivation
// that looks like a control but isn't one.
//
// `environmentBanner()` below is NOT part of that deletion and keeps its full
// behaviour unchanged: `apps/officer-web/src/pages/LoginPage.tsx` still calls
// it for the sign-in screen's badge and DANGER-only alert, which is a genuine
// anomaly alarm (a demo identity provider next to a `production` deployment
// name, or next to a live settlement dispatch) rather than the reassurance
// notice this file used to also produce — see that page's own comment for why
// it is asymmetric with the (now deleted) Shell notice.
//
// See `docs/security/SECURITY.md` S-016 for how this closes that finding: the
// UI sentence S-016 was about no longer exists anywhere for `Shell.tsx` to get
// right or wrong. What still carries the honesty this notice used to gesture
// at is entirely server-side and untouched by this change: the gateway's boot
// refusal (`assertDemonstrationModeSafe`), `PRS2_DEMONSTRATION_MODE` defaulting
// off and admitting only the exact string `'on'`, `audit.event` tagging every
// row with `demonstration_mode` at INSERT (immutable thereafter), the three
// `/api/*` responses still reporting the three fields for any client that
// asks, and `assertDemoIdentityAllowed` still refusing an unrecognised
// `APP_ENV` at boot.
